"use client";

import { useEffect, useRef, useState } from "react";

import { Progress } from "@/components/ui/progress";

interface CountdownTimerProps {
  resetSignal: boolean;
  startDelay?: number;
  cycleDuration?: number;
  endCycle?: boolean;
  onStarted: () => void;
  onCycleComplete: () => void;
  className?: string;
}

export function CountdownTimer({
  resetSignal,
  startDelay = 5,
  endCycle = false,
  cycleDuration = 10,
  onStarted,
  onCycleComplete,
  className = "",
}: CountdownTimerProps) {
  const [phase, setPhase] = useState<"initial" | "repeating" | "end">(
    "initial"
  );
  const [timeRemaining, setTimeRemaining] = useState(startDelay * 1000); // Start with 5 seconds in milliseconds
  const [isActive, setIsActive] = useState(true);

  const startTimeRef = useRef<number>(Date.now());
  const totalDurationRef = useRef<number>(startDelay * 1000); // Initial 5 seconds

  useEffect(() => {
    if (endCycle) {
      setPhase("end");
      setIsActive(false);
      setTimeRemaining(0);
    }
  }, [endCycle]);

  useEffect(() => {
    let animationFrame: number;

    if (isActive) {
      const updateTimer = () => {
        const now = Date.now();
        const elapsed = now - startTimeRef.current;
        const remaining = Math.max(0, totalDurationRef.current - elapsed);

        setTimeRemaining(remaining);

        if (remaining <= 0) {
          // Timer completed
          if (phase === "initial") {
            // Switch to repeating phase
            onStarted();
            setPhase("repeating");
            startTimeRef.current = Date.now();
            totalDurationRef.current = cycleDuration * 1000;
            setTimeRemaining(cycleDuration * 1000);
          } else {
            // Complete cycle and restart
            onCycleComplete();
            startTimeRef.current = Date.now();
            totalDurationRef.current = cycleDuration * 1000;
            setTimeRemaining(cycleDuration * 1000);
          }
        }

        if (remaining > 0) {
          animationFrame = requestAnimationFrame(updateTimer);
        }
      };

      animationFrame = requestAnimationFrame(updateTimer);
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isActive, phase, cycleDuration, onCycleComplete]);

  const totalDuration = totalDurationRef.current;
  const progress =
    totalDuration == timeRemaining
      ? 0
      : totalDuration > 0
        ? ((totalDuration - timeRemaining) / totalDuration) * 100
        : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  useEffect(() => {
    setPhase("initial");
    setTimeRemaining(5000);
    startTimeRef.current = Date.now();
    totalDurationRef.current = 5000;
    setIsActive(true);
  }, [resetSignal]);

  // const toggleTimer = () => {
  //   if (isActive) {
  //     setIsActive(false);
  //   } else {
  //     startTimeRef.current =
  //       Date.now() - (totalDurationRef.current - timeRemaining);
  //     setIsActive(true);
  //   }
  // };

  return (
    <div className={`px-6 space-y-4 ${className}`}>
      {phase === "initial" ? (
        <div className="text-center space-y-4">
          <p className="text-lg text-muted-foreground">Starting in...</p>
          <div className="text-6xl font-bold text-primary">
            {secondsRemaining}
          </div>
        </div>
      ) : (
        <div className="text-center space-y-4">
          <div className="text-4xl font-bold text-primary">
            {secondsRemaining}
          </div>
          <div className="w-full max-w-md mx-auto space-y-2">
            <Progress
              value={100 - progress}
              className="h-4 transition-all duration-75 ease-linear"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              {/* <span>{Math.round((100 - progress) * 10) / 10}% remaining</span> */}
              <span>{(timeRemaining / 1000).toFixed(1)}s</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
